import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import ActiveNumbers from './pages/ActiveNumbers';
import BuyNumber from './pages/BuyNumber';
import Dashboard from './pages/Dashboard';
import OperationsHistory from './pages/OperationsHistory';
import LongTermRental from './pages/LongTermRental';
import VoiceVerification from './pages/VoiceVerification';
import PublicLongRentalInbox from './pages/PublicLongRentalInbox';
import {
  ApiError,
  fetchActiveNumbers,
  fetchCurrentUser,
  fetchDashboard,
  isServiceVisibleForCurrentUser,
  fetchSystemInfo,
  fetchOperations,
  getApiErrorMessage,
} from './services/smsApi';
import {
  ActiveNumberItem,
  DashboardData,
  OperationItem,
  SystemInfo,
  ThemeMode,
  UserProfile,
} from './types/models';
import { initTheme, toggleTheme } from './utils/uiTheme';
import './styles/u2.css';
import './styles/sms.css';

const PORTAL_PROFILE_REVISION_KEY = 'portal:profileRevision';

function getPortalLoginUrl(): string {
  const hostname = window.location.hostname.toLowerCase();
  const isPortalHost = hostname === '3a-fb.info' || hostname.endsWith('.3a-fb.info');
  const base = isPortalHost
    ? `${window.location.protocol}//start.3a-fb.info/login`
    : `${window.location.origin}/hub/login`;
  const url = new URL(base, window.location.origin);
  url.searchParams.set('returnUrl', window.location.href);
  return url.toString();
}

type BootstrapFailureKind =
  | 'unauthorized'
  | 'service-unpublished'
  | 'auth-unavailable'
  | 'access-check-unavailable'
  | 'bootstrap-failed';

interface BootstrapFailure {
  kind: BootstrapFailureKind;
  message: string;
}

function classifyProfileFailure(error: unknown): BootstrapFailure {
  if (error instanceof ApiError && error.status === 401) {
    return {
      kind: 'unauthorized',
      message: 'Сессия недействительна или истекла. Выполните вход повторно.',
    };
  }

  if (error instanceof ApiError && error.status === 503) {
    return {
      kind: 'auth-unavailable',
      message: 'Сервис авторизации временно недоступен. Повторите попытку позже.',
    };
  }

  return {
    kind: 'bootstrap-failed',
    message: getApiErrorMessage(error),
  };
}

function classifyAccessCheckFailure(error: unknown): BootstrapFailure {
  if (error instanceof ApiError && error.status === 401) {
    return classifyProfileFailure(error);
  }

  if (error instanceof ApiError && error.status === 403) {
    return {
      kind: 'service-unpublished',
      message: 'Сервис не опубликован и доступен только администратору.',
    };
  }

  return {
    kind: 'access-check-unavailable',
    message: 'Не удалось подтвердить публикацию сервиса. Доступ временно закрыт.',
  };
}

function publishProfileRevision(): void {
  try {
    localStorage.setItem(PORTAL_PROFILE_REVISION_KEY, String(Date.now()));
  } catch {
    // localStorage может быть недоступен в отдельных режимах браузера.
  }
}

export const LegacyAdminRedirect: React.FC = () => (
  <section className="sms-card">
    <div className="sms-sectionHead">
      <h2>Администрирование SMS Resale перенесено</h2>
      <p>Управление сервисом доступно в центральной админке портала.</p>
    </div>
    <div className="u2-row" style={{ justifyContent: 'flex-start' }}>
      <a href="/portal-admin/sms-resale" className="u2-btn is-primary">
        Открыть центральную админку
      </a>
    </div>
  </section>
);

interface BootstrapFailureScreenProps {
  failure: BootstrapFailure;
  onRetry: () => void;
}

const BootstrapFailureScreen: React.FC<BootstrapFailureScreenProps> = ({
  failure,
  onRetry,
}) => {
  const isLoginRequired = failure.kind === 'unauthorized';
  const title = failure.kind === 'service-unpublished'
    ? 'Доступ ограничен'
    : failure.kind === 'auth-unavailable'
      ? 'Сервис авторизации недоступен'
      : failure.kind === 'access-check-unavailable'
        ? 'Не удалось проверить доступ'
        : isLoginRequired
          ? 'Требуется авторизация'
          : 'Не удалось загрузить SMS';

  return (
    <div className="u2-shell">
      <main className="u2-main">
        <div className="u2-container">
          <section className="sms-card">
            <div className="sms-sectionHead">
              <h2>{title}</h2>
              <p>{failure.message}</p>
            </div>
            <div className="u2-row" style={{ justifyContent: 'flex-start' }}>
              {isLoginRequired ? <a href={getPortalLoginUrl()} className="u2-btn is-primary">Перейти к входу</a> : null}
              <button type="button" className="u2-btn is-secondary" onClick={onRetry}>
                Повторить
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

interface RoutedShellProps {
  systemInfo: SystemInfo | null;
  theme: ThemeMode;
  activeNumbers: ActiveNumberItem[];
  dashboardData: DashboardData | null;
  operations: OperationItem[];
  warningMessage: string | null;
  isRefreshing: boolean;
  onThemeToggle: () => void;
  onRefresh: () => void;
  onPurchaseFinished: () => Promise<void>;
  onActiveNumbersUpdate: (items: ActiveNumberItem[]) => void;
}

const RoutedShell: React.FC<RoutedShellProps> = ({
  systemInfo,
  theme,
  activeNumbers,
  dashboardData,
  operations,
  warningMessage,
  isRefreshing,
  onThemeToggle,
  onRefresh,
  onPurchaseFinished,
  onActiveNumbersUpdate,
}) => {
  const location = useLocation();
  return (
    <AppLayout
      uiTheme={theme}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      onThemeToggle={onThemeToggle}
    >
      {location.pathname === '/admin' ? (
        <LegacyAdminRedirect />
      ) : warningMessage ? (
        <section className="sms-card">
          <div className="sms-sectionHead">
            <h2>Часть данных не обновилась</h2>
            <p className="is-negative">{warningMessage}</p>
          </div>
        </section>
      ) : null}

      {location.pathname === '/admin' ? null : <Routes>
        <Route
          path="/"
          element={(
            <Dashboard
              data={dashboardData}
              activeNumbers={activeNumbers}
            />
          )}
        />

        <Route
          path="/buy-number"
          element={(
            <BuyNumber
              onPurchaseFinished={onPurchaseFinished}
              summary={dashboardData?.summary}
            />
          )}
        />

        <Route path="/rent" element={<Navigate to="/buy-number" replace />} />

        <Route path="/long-term-rent" element={<LongTermRental />} />

        <Route path="/voice-verification" element={<VoiceVerification />} />

        <Route
          path="/active-numbers"
          element={(
            <ActiveNumbers
              numbers={activeNumbers}
              onActiveNumbersUpdate={onActiveNumbersUpdate}
              onDashboardRefresh={onPurchaseFinished}
              mode={systemInfo?.effectiveMode ?? 'live'}
            />
          )}
        />

        <Route path="/operations" element={<OperationsHistory operations={operations} />} />

        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>}
    </AppLayout>
  );
};

const AuthenticatedApp: React.FC = () => {
  const [theme, setTheme] = useState<ThemeMode>('light');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [activeNumbers, setActiveNumbers] = useState<ActiveNumberItem[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [operations, setOperations] = useState<OperationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [bootstrapFailure, setBootstrapFailure] = useState<BootstrapFailure | null>(null);
  const mountedRef = useRef<boolean>(true);
  const loadSequenceRef = useRef<number>(0);

  useEffect(() => {
    setTheme(initTheme());
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadSequenceRef.current += 1;
    };
  }, []);

  const resetAuthenticatedData = useCallback(() => {
    setUser(null);
    setDashboardData(null);
    setOperations([]);
    setActiveNumbers([]);
  }, []);

  const loadData = useCallback(async (background = false) => {
    const loadSequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = loadSequence;
    const isCurrent = () => mountedRef.current && loadSequenceRef.current === loadSequence;

    if (background) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    setWarningMessage(null);

    try {
      try {
        const system = await fetchSystemInfo();
        if (!isCurrent()) return;
        setSystemInfo(system);
      } catch {
        if (!isCurrent()) return;
        setSystemInfo(null);
      }

      let me: UserProfile;
      try {
        me = await fetchCurrentUser();
      } catch (error) {
        if (!isCurrent()) return;
        resetAuthenticatedData();
        setBootstrapFailure(classifyProfileFailure(error));
        return;
      }

      if (!isCurrent()) return;

      if (me.role !== 'admin') {
        let isServiceVisible: boolean;
        try {
          isServiceVisible = await isServiceVisibleForCurrentUser('resale-sms');
        } catch (error) {
          if (!isCurrent()) return;
          resetAuthenticatedData();
          setBootstrapFailure(classifyAccessCheckFailure(error));
          return;
        }

        if (!isCurrent()) return;

        if (!isServiceVisible) {
          resetAuthenticatedData();
          setBootstrapFailure({
            kind: 'service-unpublished',
            message: 'Сервис не опубликован и доступен только администратору.',
          });
          return;
        }
      }

      const [dashboardResult, operationsResult, activeNumbersResult] = await Promise.allSettled([
        fetchDashboard(),
        fetchOperations(),
        fetchActiveNumbers(),
      ]);
      if (!isCurrent()) return;

      setUser(me);
      setBootstrapFailure(null);

      const warnings: string[] = [];
      if (dashboardResult.status === 'fulfilled') {
        setDashboardData(dashboardResult.value);
      } else {
        warnings.push(`Главная: ${getApiErrorMessage(dashboardResult.reason)}`);
      }

      if (operationsResult.status === 'fulfilled') {
        setOperations(operationsResult.value);
      } else {
        warnings.push(`Операции: ${getApiErrorMessage(operationsResult.reason)}`);
      }

      if (activeNumbersResult.status === 'fulfilled') {
        setActiveNumbers(activeNumbersResult.value);
      } else {
        warnings.push(`Активные номера: ${getApiErrorMessage(activeNumbersResult.reason)}`);
      }
      setWarningMessage(warnings.length > 0 ? warnings.join(' | ') : null);
    } finally {
      if (!isCurrent()) return;
      setIsRefreshing(false);
      setLoading(false);
    }
  }, [resetAuthenticatedData]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'token' || event.key === PORTAL_PROFILE_REVISION_KEY) {
        loadData(true);
      }
    };

    const handleFocus = () => {
      loadData(true);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadData]);

  useEffect(() => {
    const handleAuthChange = (event: Event) => {
      const detail = (event as CustomEvent<{ authenticated?: boolean }>).detail;
      if (detail?.authenticated === false) {
        loadSequenceRef.current += 1;
        resetAuthenticatedData();
        setBootstrapFailure({ kind: 'unauthorized', message: 'Вы вышли из системы.' });
        setLoading(false);
      }
    };
    window.addEventListener('portal-auth-changed', handleAuthChange);
    return () => window.removeEventListener('portal-auth-changed', handleAuthChange);
  }, [resetAuthenticatedData]);

  const handleThemeToggle = useCallback(() => {
    setTheme((current) => toggleTheme(current));
  }, []);

  const handleRefresh = async () => {
    await loadData(true);
    publishProfileRevision();
  };

  const handlePurchaseFinished = async () => {
    await loadData(true);
    publishProfileRevision();
  };

  const handleActiveNumbersUpdate = useCallback((items: ActiveNumberItem[]) => {
    setActiveNumbers(items);
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'var(--u2-muted)',
      }}
      >
        Загрузка SMS...
      </div>
    );
  }

  if (!user) {
    const failure = bootstrapFailure || {
      kind: 'bootstrap-failed' as const,
      message: 'Не удалось получить профиль пользователя.',
    };
    return <BootstrapFailureScreen failure={failure} onRetry={() => loadData(false)} />;
  }

  return (
    <BrowserRouter
      basename={process.env.PUBLIC_URL || '/'}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <RoutedShell
        systemInfo={systemInfo}
        theme={theme}
        activeNumbers={activeNumbers}
        dashboardData={dashboardData}
        operations={operations}
        warningMessage={warningMessage}
        isRefreshing={isRefreshing}
        onThemeToggle={handleThemeToggle}
        onRefresh={handleRefresh}
        onPurchaseFinished={handlePurchaseFinished}
        onActiveNumbersUpdate={handleActiveNumbersUpdate}
      />
    </BrowserRouter>
  );
};

const App: React.FC = () => (
  /\/inbox\//.test(window.location.pathname)
    ? <PublicLongRentalInbox />
    : <AuthenticatedApp />
);

export default App;
